import { useState, Fragment } from "react";
import { useTranslations } from "next-intl";
import { Discount, Pricescheme } from "@types";
import { filterDiscountDups } from "@utils/helpers";
import { useIsShopsite } from "@context/DomainContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/accordion";
import { Heading, Text } from "@components/theming";
import PriceInformation from "./priceInformation";
import DiscountInformation from "./discountInformation";
import { flattenDepth } from "lodash";

export default function EventInfoAccordion({
  hasPaymentInfo,
  priceschemes,
  discount,
  currency,
}: {
  hasPaymentInfo: boolean;
  priceschemes?: Pricescheme[] | null;
  discount?: Discount[] | null;
  currency: string;
}) {
  const [accordionValue, setAccordionValue] = useState<string[]>([]);
  const t = useTranslations("EventPage");
  const isShopsite = useIsShopsite();

  const filtered_discounts = discount?.filter(
    (discount) =>
      discount.active &&
      (discount.combo
        ? discount.combo.active && !discount.combo.hidden
        : true) &&
      (discount.type.name === "Combo" ? !!discount.combo : true)
  );

  const hasDiscountInfo = filtered_discounts && filtered_discounts.length > 0;

  const all_active_pricesections = flattenDepth(
    priceschemes
      ?.filter((pricescheme) => pricescheme.active)
      .map((ps) =>
        ps.pricesections
          ? ps.pricesections
              .filter((pricesection) => pricesection.active)
              .map((pricesection) => ({
                ...pricesection,
                pricescheme: { id: ps.id, name: ps.name },
              }))
          : []
      ),
    2
  );

  return (
    <>
      {(hasPaymentInfo && hasDiscountInfo) || isShopsite ? (
        <>
          {!isShopsite && (
            <Heading weight="bold" size={{ initial: "7", sm: "8" }}>
              {t("information")}
            </Heading>
          )}
          <Accordion
            style={{
              scrollMarginTop: "6.5rem",
            }}
            type="multiple"
            value={accordionValue}
            onValueChange={setAccordionValue}
            whitelabelClasses="overflow-hidden rounded-xl"
            shopsiteClasses="overflow-hidden mt-10 border bg-white shadow-sm rounded-xl"
          >
            {hasPaymentInfo ? (
              <AccordionItem value="Payment" className="!border-b-0">
                <AccordionTrigger
                  className="relative group flex w-full justify-between gap-x-6 px-4 py-5 sm:px-6"
                  shopsiteClasses="sm:enabled:hover:bg-gray-50"
                >
                  <Text
                    as="span"
                    size="5"
                    weight="bold"
                    align="left"
                    shopsiteClasses="text-black text-lg font-semibold leading-7"
                  >
                    {t("priceinformation.heading")}
                  </Text>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 items-start p-4 sm:hidden">
                    {priceschemes
                      ?.filter((pricescheme) => pricescheme.active)
                      .map((pricescheme, index) => (
                        <Fragment key={`${pricescheme.id}-${index}`}>
                          {pricescheme.pricesections
                            ?.filter((pricesection) => pricesection.active)
                            .map((pricesection) => (
                              <PriceInformation
                                key={pricesection.id}
                                pricesection={pricesection}
                                priceschemename={pricescheme.name}
                                currency={currency}
                              />
                            ))}
                        </Fragment>
                      ))}
                  </div>
                  <div className="hidden sm:grid grid-cols-2 gap-8 p-4">
                    <div className="flex flex-col gap-8">
                      {all_active_pricesections
                        .filter((_, index) => index % 2 === 0)
                        .map((pricesection) => (
                          <PriceInformation
                            key={`${pricesection.pricescheme.id}-${pricesection.id}`}
                            pricesection={pricesection}
                            priceschemename={pricesection.pricescheme.name}
                            currency={currency}
                          />
                        ))}
                    </div>
                    <div className="flex flex-col gap-8">
                      {all_active_pricesections
                        .filter((_, index) => index % 2 !== 0)
                        .map((pricesection) => (
                          <PriceInformation
                            key={`${pricesection.pricescheme.id}-${pricesection.id}`}
                            pricesection={pricesection}
                            priceschemename={pricesection.pricescheme.name}
                            currency={currency}
                          />
                        ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ) : (
              <></>
            )}
            {hasDiscountInfo ? (
              <AccordionItem
                value="Discount"
                className="!border-b-0"
                shopsiteClasses={hasPaymentInfo ? "!border-t" : ""}
              >
                <AccordionTrigger
                  className="relative group flex w-full justify-between gap-x-6 px-4 py-5 sm:px-6"
                  shopsiteClasses="sm:enabled:hover:bg-gray-50"
                >
                  <Text
                    as="span"
                    size="5"
                    weight="bold"
                    align="left"
                    shopsiteClasses="text-black text-lg font-semibold leading-7"
                  >
                    {t("discountinformation")}
                  </Text>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 items-start p-4">
                    {filterDiscountDups(filtered_discounts).map((discount) => (
                      <DiscountInformation
                        key={discount.id}
                        discount={
                          discount.combo
                            ? {
                                id: discount.combo.id,
                                name: discount.combo.name,
                                description: discount.combo.description,
                              }
                            : {
                                id: discount.id,
                                name: discount.name,
                                description: discount.description,
                              }
                        }
                      />
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            ) : (
              <></>
            )}
          </Accordion>
        </>
      ) : hasPaymentInfo ? (
        <>
          <Heading
            weight="bold"
            size={{ initial: "7", sm: "8" }}
            shopsiteClasses="text-black text-lg font-semibold leading-7"
          >
            {t("priceinformation.heading")}
          </Heading>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 items-start">
            {priceschemes
              ?.filter((pricescheme) => pricescheme.active)
              .map((pricescheme, index) => (
                <Fragment key={`${pricescheme.id}-${index}`}>
                  {pricescheme.pricesections
                    ?.filter((pricesection) => pricesection.active)
                    .map((pricesection) => (
                      <PriceInformation
                        key={pricesection.id}
                        pricesection={pricesection}
                        priceschemename={pricescheme.name}
                        currency={currency}
                      />
                    ))}
                </Fragment>
              ))}
          </div>
        </>
      ) : hasDiscountInfo ? (
        <>
          <Heading
            weight="bold"
            size={{ initial: "7", sm: "8" }}
            shopsiteClasses="text-black text-lg font-semibold leading-7"
          >
            {t("discountinformation")}
          </Heading>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 items-start">
            {filterDiscountDups(filtered_discounts).map((discount) => (
              <DiscountInformation
                key={discount.id}
                discount={
                  discount.combo
                    ? {
                        id: discount.combo.id,
                        name: discount.combo.name,
                        description: discount.combo.description,
                      }
                    : {
                        id: discount.id,
                        name: discount.name,
                        description: discount.description,
                      }
                }
              />
            ))}
          </div>
        </>
      ) : (
        <></>
      )}
    </>
  );
}
